import * as React from "react";
import { ReactElement, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useKeyPressCallback } from "~/lib/keyPress";
import style from "./index.module.scss";

type ModalId = number;

let LastModalId: ModalId = 0;

export const generatePopupId = (): ModalId => LastModalId++;

type ModalExtraProps = {
  size?: "default" | "wide";
};

interface IModalContext {
  show: <T>(
    modalId: ModalId,
    component: IModalComponent<T>,
    componentProps: IModalComponentProps<T>,
    modal?: React.HTMLAttributes<HTMLDivElement> & ModalExtraProps
  ) => void;
  close: (modalId: ModalId) => void;
  isOpen: (modalId: ModalId) => boolean;
}

export const ModalContext = React.createContext<IModalContext>({} as IModalContext);

interface IModalProviderProps {
  children: ReactNode;
}

type IModalProviderState = [ModalId, ReactElement | null][];

export type IModalComponentClose = () => void;

export type IModalComponentProps<P> = P & { close?: IModalComponentClose };

export type IModalComponent<P> = (props: IModalComponentProps<P>, context?: any) => ReactElement | null;

export type IModalsHook = {
  create: <T>(component: IModalComponent<T>, defaultProps?: T) => IModal<T>;
};

export type IModal<T> = {
  show: (props?: T, modal?: React.HTMLAttributes<HTMLDivElement> & ModalExtraProps) => void;
  close: () => void;
  isOpen: () => boolean;
};

// TODO: might be better just to refactor all this shit.
export function useModals(): IModalsHook {
  const modalContext = useContext(ModalContext);
  return {
    create: (component, defaultProps) => {
      const [modalId, setModalId] = useState<ModalId | null>(null);
      const closeByModalId = (closeModalId: ModalId) => {
        if (closeModalId !== null) {
          modalContext.close(closeModalId);
          setModalId(null);
        }
      };
      return {
        show: (props, modal) => {
          if (modalId === null) {
            let newModalId = generatePopupId();
            modalContext.show(
              newModalId,
              component,
              {
                ...defaultProps,
                ...props,
                close: () => closeByModalId(newModalId),
              },
              modal
            );
            setModalId(newModalId);
          }
        },
        close: () => closeByModalId(modalId),
        isOpen: () => modalContext.isOpen(modalId),
      };
    },
  };
}

interface IModalProps extends React.HTMLAttributes<HTMLDivElement>, ModalExtraProps {
  close: () => void;
}

export const Modal = ({ close, size = "default", ...props }: IModalProps) => {
  useKeyPressCallback("Escape", close);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    const id = window.setTimeout(() => {
      if (!document.body.contains(node)) return;

      if (node.contains(document.activeElement)) return;

      const focusable = node.querySelector<HTMLElement>(
        "input, textarea, button, [href], select, [tabindex]:not([tabindex='-1'])"
      );

      (focusable || node).focus();
    }, 0);

    return () => window.clearTimeout(id);
  }, []);
  return (
    <div
      {...props}
      ref={contentRef}
      tabIndex={-1}
      className={[
        style.modal,
        size === "wide" && style.modalWide,
        props.className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {props.children}
    </div>
  );
};

export const ModalProvider = (props: IModalProviderProps) => {
  const [modals, setModals] = useState<IModalProviderState>([]);

  return (
    <ModalContext.Provider
      value={{
        show: (modalId, Component, componentProps, modal) => {
          setModals(prev => [
            ...prev,
            [
              modalId,
              <Modal {...modal} close={componentProps.close!}>
                {React.createElement(Component, { ...componentProps })}
              </Modal>,
            ],
          ]);
          return modalId;
        },
        isOpen: (modalId) => {
          return modals.some(([id, _]) => id === modalId);
        },
        close: (hideModalId: ModalId) => {
          setModals(prev => prev.filter(([modalId, _]) => modalId !== hideModalId));
        },
      }}
    >
      {props.children}
      {modals.map(([modalId, component]) => (
        <div key={modalId} className={style.modalWrapper} style={{ zIndex: 9999 + modalId }}>
          {component}
        </div>
      ))}
    </ModalContext.Provider>
  );
};
